import React, { useEffect, useState, useCallback } from "react";
import { client, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserCog, Plus, Trash2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "ministry_admin", label: "Ministry Admin" },
  { value: "church_admin", label: "Church Admin" },
  { value: "content_manager", label: "Content Manager" },
  { value: "department_manager", label: "Department Manager" },
  { value: "followup_team", label: "Follow-Up / Prayer Team" },
];

const Users = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "content_manager", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/auth/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", role: "content_manager", password: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, role: u.role, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await client.put(`/auth/users/${editing.id}`, payload);
      } else {
        await client.post("/auth/users", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await client.delete(`/auth/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-brand-orange" />
          <h1 className="font-display text-2xl font-bold text-navy">Users & Roles</h1>
        </div>
        <Button onClick={openNew} data-testid="add-user-btn" className="rounded-sm bg-brand-orange hover:bg-brand-orange-600"><Plus className="mr-1 h-4 w-4" /> Add User</Button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div> : (
        <div className="overflow-hidden rounded-sm border border-border bg-white">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-secondary/60 text-left">
              <tr>
                {["Name", "Email", "Role"].map((h) => <th key={h} className="px-4 py-3 font-display text-xs font-semibold uppercase tracking-wide text-navy">{h}</th>)}
                <th className="px-4 py-3 text-right font-display text-xs font-semibold uppercase tracking-wide text-navy">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="px-4 py-3 font-medium text-navy">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3"><span className="rounded-sm bg-secondary px-2 py-0.5 text-xs capitalize">{u.role.replace("_", " ")}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                      {u.email !== me?.email && <Button variant="ghost" size="icon" onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label>Email</Label><Input data-testid="user-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{editing ? "New Password (optional)" : "Password"}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="rounded-sm bg-navy hover:bg-navy-surface">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
